import { Link } from 'react-router';
import type { AuditEventDTO } from '@devrobot/shared';
import { CircleCheck, CircleX, ScrollText, ShieldBan } from 'lucide-react';
import { useAudit, type AuditFilters } from '../../api/queries';
import { EmptyState } from '../../components/EmptyState';
import { ErrorAlert } from '../../components/ErrorAlert';
import { RelativeTime } from '../../components/RelativeTime';
import { SkeletonTable } from '../../components/Skeleton';
import { BADGE_CLASS, statusInfo } from '../../lib/status';

const OUTCOME_ICON = { success: CircleCheck, failure: CircleX, denied: ShieldBan } as const;

/** Outcome as icon + label (never colour alone). */
function OutcomeBadge({ outcome }: { outcome: AuditEventDTO['outcome'] }) {
  const info = statusInfo('outcome', outcome);
  const Icon = OUTCOME_ICON[outcome] ?? CircleCheck;
  return (
    <span className={`${BADGE_CLASS[info.tone]} badge-xs gap-1 whitespace-nowrap`} title={info.hint} data-tone={info.tone}>
      <Icon className="size-3" aria-hidden="true" />
      {info.label}
    </span>
  );
}

function DetailsCell({ details }: { details?: Record<string, unknown> }) {
  if (!details || Object.keys(details).length === 0) return <span className="text-base-content/40">—</span>;
  const summary = Object.entries(details)
    .slice(0, 3)
    .map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
    .join(' ');
  return (
    <details className="max-w-xs">
      <summary className="cursor-pointer truncate text-xs text-base-content/65">{summary}</summary>
      <pre className="mt-1 animate-rise rounded-field bg-base-200/60 p-2 text-xs whitespace-pre-wrap break-anywhere">{JSON.stringify(details, null, 2)}</pre>
    </details>
  );
}

function Actor({ e }: { e: AuditEventDTO }) {
  return e.actor ? <span title={`@${e.actor.username}`}>{e.actor.name || e.actor.username}</span> : <span className="text-base-content/60">system</span>;
}

function Scope({ e }: { e: AuditEventDTO }) {
  return (
    <>
      {e.project ? (
        <Link className="link link-hover" to={`/projects/${e.project}`}>
          {e.project}
        </Link>
      ) : null}
      {e.environment ? <span className="text-base-content/65"> / {e.environment}</span> : null}
      {e.targetId ? (
        <div>
          <Link className="link link-hover text-xs" to={`/targets/${e.targetId}`}>
            target
          </Link>
        </div>
      ) : null}
      {e.vpsId ? (
        <div>
          <Link className="link link-hover text-xs" to={`/vps/${e.vpsId}`}>
            VPS
          </Link>
        </div>
      ) : null}
      {!e.project && !e.targetId && !e.vpsId ? <span className="text-base-content/40">platform</span> : null}
    </>
  );
}

function Row({ e }: { e: AuditEventDTO }) {
  return (
    <tr className="align-top transition-colors duration-150 hover:bg-base-200/50">
      <td className="text-sm whitespace-nowrap text-base-content/65">
        <RelativeTime value={e.ts} />
      </td>
      <td className="text-sm">
        <Actor e={e} />
      </td>
      <td className="font-mono text-xs">{e.action}</td>
      <td>
        <OutcomeBadge outcome={e.outcome} />
      </td>
      <td className="text-sm">
        <Scope e={e} />
      </td>
      <td className="text-xs text-base-content/65 max-xl:hidden">{e.source ?? '—'}</td>
      <td>
        <DetailsCell details={e.details} />
      </td>
    </tr>
  );
}

/** Phones: each event as a stacked row. */
function Item({ e }: { e: AuditEventDTO }) {
  return (
    <li className="flex flex-col gap-1.5 px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <span className="min-w-0 font-mono text-xs font-semibold break-anywhere">{e.action}</span>
        <OutcomeBadge outcome={e.outcome} />
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-base-content/60">
        <Actor e={e} />
        <span aria-hidden="true">·</span>
        <RelativeTime value={e.ts} />
        {e.source ? (
          <>
            <span aria-hidden="true">·</span>
            <span>{e.source}</span>
          </>
        ) : null}
      </div>
      <div className="text-xs">
        <Scope e={e} />
      </div>
      {e.details && Object.keys(e.details).length > 0 ? <DetailsCell details={e.details} /> : null}
    </li>
  );
}

/** Audit events newest first, with "load more" paging by timestamp. */
export function AuditLog({ filters }: { filters: AuditFilters }) {
  const q = useAudit(filters);
  if (q.isPending) return <SkeletonTable />;
  if (q.isError) return <ErrorAlert error={q.error} onRetry={() => void q.refetch()} />;
  const items = q.data.pages.flat();
  if (items.length === 0) {
    return (
      <EmptyState icon={ScrollText} title="No audit events">
        {filters.action || filters.actor ? 'No events match these filters.' : 'Actions such as deployments, configuration changes and secret reveals are recorded here.'}
      </EmptyState>
    );
  }
  return (
    <div className="card dr-surface overflow-hidden">
      <ul className="divide-y divide-hairline md:hidden" aria-label="Audit events">
        {items.map((e) => (
          <Item key={e.id} e={e} />
        ))}
      </ul>
      <div className="overflow-x-auto max-md:hidden">
        <table className="table table-sm">
          <thead>
            <tr className="text-xs tracking-wide text-base-content/50 uppercase">
              <th>When</th>
              <th>Actor</th>
              <th>Action</th>
              <th>Outcome</th>
              <th>Scope</th>
              <th className="max-xl:hidden">Source</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <Row key={e.id} e={e} />
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-hairline px-4 py-3 text-sm text-base-content/60 sm:px-5">
        <span>{items.length} events shown. Secret values are never recorded.</span>
        {q.hasNextPage ? (
          <button type="button" className="btn btn-sm" onClick={() => void q.fetchNextPage()} disabled={q.isFetchingNextPage}>
            {q.isFetchingNextPage ? <span className="loading loading-spinner loading-xs" aria-hidden="true" /> : null}
            Load older events
          </button>
        ) : (
          <span>End of the log.</span>
        )}
      </div>
    </div>
  );
}
